import { IconDeviceDesktop, IconMoonStars, IconSun } from '@tabler/icons-react';
import type { ComponentPropsWithRef, ComponentType } from 'react';

import { cn } from '../../lib/cn';
import type { ThemeChoice } from '../../theme/theme';
import { useTranslation } from '../../i18n/LocaleProvider';
import type { DsTranslationKey } from '../../i18n/catalogues/en';
import { useTheme } from '../../theme/ThemeProvider';
import { IconButton, type IconButtonSize, type IconButtonVariant } from '../core/IconButton';

/** The order the button cycles through: light → dark → system → light. */
const NEXT: Record<ThemeChoice, ThemeChoice> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

/** The catalogue keys for the three states, in their two wordings. */
const CHOICE_NAME_KEY: Record<ThemeChoice, DsTranslationKey> = {
  light: 'ds.theme.name.light',
  dark: 'ds.theme.name.dark',
  system: 'ds.theme.name.system',
};

/** The one-word form of the same three states, for the visible caption. */
const CHOICE_TEXT_KEY: Record<ThemeChoice, DsTranslationKey> = {
  light: 'ds.theme.light',
  dark: 'ds.theme.dark',
  system: 'ds.theme.system',
};

const ICONS: Record<ThemeChoice, ComponentType<{ className?: string }>> = {
  light: IconSun,
  dark: IconMoonStars,
  system: IconDeviceDesktop,
};

export interface ThemeSwitcherProps
  extends Omit<ComponentPropsWithRef<'button'>, 'children' | 'type' | 'onClick'> {
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /**
   * Replaces the generated accessible name. The default is
   * `Theme: {current}. Switch to {next}.` — an icon-only control has no visible
   * text, so that name is the only thing a screen reader has to go on, and it
   * has to say which theme is *on*, not just that this is a theme button.
   */
  label?: string;
  /**
   * Shows the current state as visible text beside the glyph.
   *
   * Three glyphs (sun, moon, monitor) say *which* theme is on but nothing about
   * what the control does, and on a touch device the `title` is unreachable. The
   * caller decides whether there is width for the caption — the header shows it
   * from `sm` up and drops it on the narrow layout, where the glyph is all the
   * room there is.
   */
  showState?: boolean;
}

/** The §37 control, wired to the app's own `useTheme()`. */
export function ThemeSwitcher({
  variant = 'ghost',
  size = 'md',
  label,
  showState = false,
  className,
  ...rest
}: ThemeSwitcherProps) {
  const { choice, cycle } = useTheme();
  const { t } = useTranslation();
  const Icon = ICONS[choice];

  return (
    <IconButton
      {...rest}
      label={
        label ??
        t('ds.theme.name', {
          current: t(CHOICE_NAME_KEY[choice]),
          next: t(CHOICE_NAME_KEY[NEXT[choice]]),
        })
      }
      icon={<Icon className="size-full" />}
      text={showState ? t(CHOICE_TEXT_KEY[choice]) : undefined}
      variant={variant}
      size={size}
      onClick={cycle}
      className={cn('text-muted hover:text-heading', className)}
    />
  );
}
